import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../api";
import { useAuth } from "../context/AuthContext";
import { reportRuntimeError } from "../reportRuntimeError";
import { throwIfFatalError, useFatalErrorState } from "../useFatalErrorState";
import {
  ASSIGNED_WORKFLOW_STATUS_OPTIONS,
  buildSortedProjectOptions,
  buildSupportCaseConsoleUrl,
  formatProjectFilterOptionLabel,
  formatProjectOptionChoiceLabel,
  collectQuarantineQuotaRequestFilterOptions,
  filterQuarantineQuotaRequestRows,
  filterQuotaRequestRowsByInboxSegment,
  formatLastMessageAtRelativePhrase,
  formatRequestedAtDateOnly,
  getTableRowBackgroundColorForAwsStatus,
  PROJECT_DISPLAY_UNASSIGNED_LABEL,
  PROJECT_SELECT_NEW_PROJECT_LABEL,
  PROJECT_SELECT_NEW_PROJECT_VALUE,
  PROJECT_SELECT_UNASSIGNED_VALUE,
  QUARANTINE_QUOTA_REQUEST_FILTER_COLUMNS,
  QUOTA_REQUEST_DEFAULT_PRIORITY,
  QUOTA_REQUEST_PRIORITY_OPTIONS,
  resolveDefaultInboxSegment,
  resolveProjectSelectValue,
  resolveQuotaNameDisplayForRow,
  resolveRequestedAtEpochSeconds,
  shouldShowNewProjectOption,
  shouldShowUnassignedProjectOption,
  sortQuarantineQuotaRequestRows,
  isSupportCaseMissingForRow,
  SUPPORT_CASE_MISSING_LABEL,
  WORKFLOW_STATUS_MUST_RESPOND,
  WORKFLOW_STATUS_QUARANTINE,
  WORKFLOW_STATUS_TRACKED,
  type ProjectOption,
  type QuotaRequestInboxSegment,
  type QuarantineQuotaRequestFilterColumn,
  type QuarantineQuotaRequestFilterState,
  type QuarantineQuotaRequestProjectNameById,
  type QuarantineQuotaRequestRow,
  type QuarantineQuotaRequestSortColumn,
  type QuarantineQuotaRequestSortDirection,
  type QuarantineQuotaRequestSortState,
} from "./quarantineQuotaRequestTable";

function ListingTableSearchInput({
  disabled,
  value,
  onValueChange,
}: {
  disabled: boolean;
  value: string;
  onValueChange: (nextValue: string) => void;
}) {
  return (
    <input
      type="search"
      value={value}
      disabled={disabled}
      onChange={(changeEvent) => {
        onValueChange(changeEvent.target.value);
      }}
      placeholder="Search quota requests"
      aria-label="Search quota requests"
      style={{
        minWidth: "14rem",
        padding: "0.25rem 0.5rem",
        border: "1px solid #ccc",
        borderRadius: "4px",
        marginLeft: "0.5rem",
      }}
    />
  );
}

function ListingTableRefreshButton({
  disabled,
  onRefresh,
}: {
  disabled: boolean;
  onRefresh: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onRefresh}
      aria-label="Refresh listing table"
      title="Refresh listing table"
      style={{
        color: "inherit",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0.25rem",
        border: "1px solid #ccc",
        borderRadius: "4px",
        background: "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="currentColor"
          d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"
        />
      </svg>
    </button>
  );
}

function SupportCaseConsoleLinkIcon({
  supportCaseConsoleUrl,
}: {
  supportCaseConsoleUrl: string;
}) {
  return (
    <a
      href={supportCaseConsoleUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Open AWS Support case in console"
      title="Open AWS Support case in console"
      style={{
        color: "inherit",
        display: "inline-flex",
        alignItems: "center",
        marginLeft: "0.35rem",
        verticalAlign: "middle",
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="currentColor"
          d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3zm-2 2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7h-2v7H5V7h7V5z"
        />
      </svg>
    </a>
  );
}

const QUARANTINE_TABLE_COLUMNS: {
  column: QuarantineQuotaRequestSortColumn;
  label: string;
}[] = [
  { column: "requested_at", label: "Requested" },
  { column: "deployment_environment_name", label: "Environment" },
  { column: "aws_region", label: "Region" },
  { column: "quota_name", label: "Quota name" },
  { column: "aws_status", label: "AWS status" },
  { column: "requested_value", label: "Value" },
  { column: "project", label: "Project" },
  { column: "status", label: "Status" },
  { column: "priority", label: "Priority" },
];

const LAST_MESSAGE_COLUMN_LABEL = "Last message";

const INBOX_SEGMENT_PENDING: QuotaRequestInboxSegment = "pending";
const INBOX_SEGMENT_QUARANTINE: QuotaRequestInboxSegment = "quarantine";

const BULK_ACTION_EMPTY_VALUE = "";
const BULK_ACTION_ASSIGN_TO_PROJECT = "assign_to_project";
const BULK_ACTION_SET_STATUS = "set_status";
const BULK_ACTION_SET_PRIORITY = "set_priority";
const BULK_ACTION_PLACEHOLDER_LABEL = "Bulk action…";
const BULK_ACTION_ASSIGN_TO_PROJECT_LABEL = "Assign to project";
const BULK_ACTION_SET_STATUS_LABEL = "Set status";
const BULK_ACTION_SET_PRIORITY_LABEL = "Set priority";

const INBOX_LISTING_TOOLBAR_ROW_MIN_HEIGHT = "2.25rem";

const BULK_ACTION_PRIMARY_SLOT_MIN_WIDTH = "11.5rem";

const BULK_ACTION_DETAIL_SLOT_MIN_WIDTH = "24rem";

const INBOX_TOOLBAR_FIELD_SEPARATOR_STYLE = {
  color: "#aaa",
  margin: "0 0.75rem",
} as const;

function formatWorkflowStatusOptionLabel(statusOption: string) {
  if (statusOption === WORKFLOW_STATUS_MUST_RESPOND) {
    return "must respond";
  }

  return statusOption;
}

const INBOX_SEGMENT_TAB_STYLE_BASE = {
  padding: "0.4rem 1rem",
  border: "1px solid #ccc",
  background: "#f5f5f5",
  cursor: "pointer",
  font: "inherit",
} as const;

const INBOX_SEGMENT_TAB_SELECTED_STYLE = {
  background: "#fff",
  fontWeight: 700,
} as const;

const INBOX_SEGMENT_TAB_BADGE_STYLE = {
  marginLeft: "0.4rem",
  padding: "0.1rem 0.45rem",
  borderRadius: "999px",
  fontSize: "0.85em",
  fontWeight: 600,
  background: "#d8d8d8",
  lineHeight: 1.2,
} as const;

const QUARANTINE_FILTER_COLUMN_LABELS: Record<QuarantineQuotaRequestFilterColumn, string> = {
  deployment_environment_name: "Environment",
  aws_region: "Region",
  quota_name: "Quota name",
  aws_status: "AWS status",
  project: "Project",
  status: "Status",
  priority: "Priority",
};

const DEFAULT_FILTERABLE_COLUMN_MAX_WIDTH = "14rem";

const QUOTA_NAME_COLUMN_MAX_WIDTH = "7rem";

const ENVIRONMENT_COLUMN_MAX_WIDTH = "9.8rem";

const REGION_COLUMN_MAX_WIDTH = "9.8rem";

const REQUESTED_VALUE_COLUMN_MAX_WIDTH = "3.5rem";

const CONSTRAINED_COLUMN_CELL_STYLE_BASE = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
};

function buildConstrainedColumnCellStyle(maxWidth: string) {
  return {
    ...CONSTRAINED_COLUMN_CELL_STYLE_BASE,
    maxWidth,
  };
}

const QUOTA_NAME_COLUMN_CELL_STYLE = buildConstrainedColumnCellStyle(QUOTA_NAME_COLUMN_MAX_WIDTH);

const ENVIRONMENT_COLUMN_CELL_STYLE = buildConstrainedColumnCellStyle(ENVIRONMENT_COLUMN_MAX_WIDTH);

const REGION_COLUMN_CELL_STYLE = buildConstrainedColumnCellStyle(REGION_COLUMN_MAX_WIDTH);

const REQUESTED_VALUE_COLUMN_CELL_STYLE = buildConstrainedColumnCellStyle(
  REQUESTED_VALUE_COLUMN_MAX_WIDTH,
);

const TABLE_CELL_PADDING_RIGHT = "0.75rem";

const TABLE_HEADER_CELL_STYLE = {
  textAlign: "left" as const,
  borderBottom: "1px solid #ccc",
  paddingRight: TABLE_CELL_PADDING_RIGHT,
};

const TABLE_BODY_CELL_STYLE = {
  paddingTop: "0.35rem",
  paddingBottom: "0.35rem",
  paddingRight: TABLE_CELL_PADDING_RIGHT,
};

function buildTableBodyCellStyle(
  constrainedColumnCellStyle?: ReturnType<typeof buildConstrainedColumnCellStyle>,
) {
  if (constrainedColumnCellStyle === undefined) {
    return TABLE_BODY_CELL_STYLE;
  }

  return { ...TABLE_BODY_CELL_STYLE, ...constrainedColumnCellStyle };
}

function resolveColumnMaxWidth(column: QuarantineQuotaRequestSortColumn): string | undefined {
  switch (column) {
    case "deployment_environment_name":
      return ENVIRONMENT_COLUMN_MAX_WIDTH;
    case "aws_region":
      return REGION_COLUMN_MAX_WIDTH;
    case "quota_name":
      return QUOTA_NAME_COLUMN_MAX_WIDTH;
    case "requested_value":
      return REQUESTED_VALUE_COLUMN_MAX_WIDTH;
    default:
      return undefined;
  }
}

function buildTableHeaderCellStyleForColumn(
  column: QuarantineQuotaRequestSortColumn,
): typeof TABLE_HEADER_CELL_STYLE {
  const maxWidth = resolveColumnMaxWidth(column);

  if (maxWidth === undefined) {
    return TABLE_HEADER_CELL_STYLE;
  }

  return { ...TABLE_HEADER_CELL_STYLE, ...buildConstrainedColumnCellStyle(maxWidth) };
}

function buildFilterSelectStyleForColumn(column: QuarantineQuotaRequestSortColumn) {
  const columnMaxWidth = resolveColumnMaxWidth(column);

  return {
    width: "100%",
    maxWidth: columnMaxWidth ?? DEFAULT_FILTERABLE_COLUMN_MAX_WIDTH,
  };
}

const SORTABLE_HEADER_BUTTON_STYLE = {
  background: "none",
  border: "none",
  padding: 0,
  font: "inherit",
  fontWeight: 700,
  cursor: "pointer",
  textAlign: "left" as const,
};

function buildNextSortState(
  currentSortState: QuarantineQuotaRequestSortState | null,
  column: QuarantineQuotaRequestSortColumn,
): QuarantineQuotaRequestSortState {
  if (currentSortState?.column === column) {
    const nextDirection: QuarantineQuotaRequestSortDirection =
      currentSortState.direction === "asc" ? "desc" : "asc";

    return { column, direction: nextDirection };
  }

  return { column, direction: "asc" };
}

function formatSortIndicator(
  sortState: QuarantineQuotaRequestSortState | null,
  column: QuarantineQuotaRequestSortColumn,
): string {
  if (sortState?.column !== column) {
    return "";
  }

  return sortState.direction === "asc" ? " ▲" : " ▼";
}

function isQuarantineQuotaRequestFilterColumn(
  column: QuarantineQuotaRequestSortColumn,
): column is QuarantineQuotaRequestFilterColumn {
  return QUARANTINE_QUOTA_REQUEST_FILTER_COLUMNS.includes(column as QuarantineQuotaRequestFilterColumn);
}

function mergeQuotaRequestRowIntoList(
  rowList: QuarantineQuotaRequestRow[],
  updatedRow: QuarantineQuotaRequestRow,
): QuarantineQuotaRequestRow[] {
  return rowList.map((row) => {
    if (row.id === updatedRow.id) {
      return updatedRow;
    }

    return row;
  });
}

export function QuarantinePage() {
  const { token, user, logout } = useAuth();
  const [rows, setRows] = useState<QuarantineQuotaRequestRow[]>([]);
  const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([]);
  const [rowActionErrorMessage, setRowActionErrorMessage] = useState<string | null>(null);
  const [fatalError, failWithError] = useFatalErrorState();
  const [isLoading, setIsLoading] = useState(true);
  const [isListingRefreshing, setIsListingRefreshing] = useState(false);
  const [listingSearchQuery, setListingSearchQuery] = useState("");
  const [searchResultRows, setSearchResultRows] = useState<QuarantineQuotaRequestRow[] | null>(
    null,
  );
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [sortState, setSortState] = useState<QuarantineQuotaRequestSortState | null>(null);
  const [filterState, setFilterState] = useState<QuarantineQuotaRequestFilterState>({});
  const [inboxSegment, setInboxSegment] = useState<QuotaRequestInboxSegment>(INBOX_SEGMENT_PENDING);
  const [showClosed, setShowClosed] = useState(false);
  const [listingSortId, setListingSortId] = useState<string | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<ReadonlySet<string>>(() => new Set());
  const [bulkActionValue, setBulkActionValue] = useState(BULK_ACTION_EMPTY_VALUE);
  const [bulkAssignProjectSelectValue, setBulkAssignProjectSelectValue] = useState(
    BULK_ACTION_EMPTY_VALUE,
  );
  const [bulkStatusSelectValue, setBulkStatusSelectValue] = useState(
    ASSIGNED_WORKFLOW_STATUS_OPTIONS[0],
  );
  const [bulkPrioritySelectValue, setBulkPrioritySelectValue] = useState(
    QUOTA_REQUEST_DEFAULT_PRIORITY,
  );
  const [isBulkActionRunning, setIsBulkActionRunning] = useState(false);
  const hasAppliedInitialInboxSegmentRef = useRef(false);

  function resetBulkSelectionAndUi() {
    setSelectedRowIds(new Set());
    setBulkActionValue(BULK_ACTION_EMPTY_VALUE);
    setBulkAssignProjectSelectValue(BULK_ACTION_EMPTY_VALUE);
    setBulkStatusSelectValue(ASSIGNED_WORKFLOW_STATUS_OPTIONS[0]);
    setBulkPrioritySelectValue(QUOTA_REQUEST_DEFAULT_PRIORITY);
  }

  function applyUpdatedQuotaRequestRowToListing(updatedRow: QuarantineQuotaRequestRow) {
    setRows((currentRows) => mergeQuotaRequestRowIntoList(currentRows, updatedRow));
    setSearchResultRows((currentSearchResultRows) => {
      if (currentSearchResultRows === null) {
        return null;
      }

      return mergeQuotaRequestRowIntoList(currentSearchResultRows, updatedRow);
    });
  }

  function handleRowSelectionToggle(rowId: string, isSelected: boolean) {
    setSelectedRowIds((currentSelectedRowIds) => {
      const nextSelectedRowIds = new Set(currentSelectedRowIds);

      if (isSelected) {
        nextSelectedRowIds.add(rowId);
      } else {
        nextSelectedRowIds.delete(rowId);
      }

      return nextSelectedRowIds;
    });
  }

  const sortedProjectOptions = useMemo(
    () => buildSortedProjectOptions(projectOptions),
    [projectOptions],
  );

  const projectNameById = useMemo((): QuarantineQuotaRequestProjectNameById => {
    const nameById: QuarantineQuotaRequestProjectNameById = {};

    for (const projectOption of projectOptions) {
      nameById[projectOption.id] = projectOption.name;
    }

    return nameById;
  }, [projectOptions]);

  async function loadInboxListingSnapshot(): Promise<{
    loadedRows: QuarantineQuotaRequestRow[];
    projectOptions: ProjectOption[];
  } | null> {
    if (!token) {
      return null;
    }

    const includeClosedInFetch =
      inboxSegment === INBOX_SEGMENT_PENDING && showClosed;
    const quotaRequestList = await api.listAllVisibleQuotaRequests(
      token,
      includeClosedInFetch,
    );
    const projectList = await api.listProjects(token);
    const loadedRows = quotaRequestList as QuarantineQuotaRequestRow[];

    return {
      loadedRows,
      projectOptions: projectList.map((projectRecord) => ({
        id: projectRecord.id,
        name: projectRecord.name,
        created_at_timestamp: projectRecord.created_at_timestamp,
      })),
    };
  }

  useEffect(() => {
    let isCancelled = false;

    async function loadQuarantineRows() {
      setIsLoading(true);

      try {
        const inboxListingSnapshot = await loadInboxListingSnapshot();

        if (!isCancelled && inboxListingSnapshot !== null) {
          setRows(inboxListingSnapshot.loadedRows);
          setProjectOptions(inboxListingSnapshot.projectOptions);

          if (!hasAppliedInitialInboxSegmentRef.current) {
            setInboxSegment(resolveDefaultInboxSegment(inboxListingSnapshot.loadedRows));
            hasAppliedInitialInboxSegmentRef.current = true;
          }
        }
      } catch (loadError) {
        if (!isCancelled) {
          failWithError(loadError);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadQuarantineRows();

    return () => {
      isCancelled = true;
    };
  }, [token, showClosed, inboxSegment, failWithError]);

  useEffect(() => {
    const trimmedListingSearchQuery = listingSearchQuery.trim();

    if (trimmedListingSearchQuery === "") {
      setSearchResultRows(null);
      setIsSearchLoading(false);

      return;
    }

    if (!token) {
      return;
    }

    let isCancelled = false;
    const debounceTimeoutId = window.setTimeout(() => {
      async function runListingSearch() {
        setIsSearchLoading(true);

        try {
          const searchResponseRows = await api.searchQuotaRequests(
            token,
            trimmedListingSearchQuery,
          );
          const loadedSearchRows = searchResponseRows as QuarantineQuotaRequestRow[];

          if (!isCancelled) {
            setSearchResultRows(loadedSearchRows);
          }
        } catch (searchError) {
          if (!isCancelled) {
            failWithError(searchError);
          }
        } finally {
          if (!isCancelled) {
            setIsSearchLoading(false);
          }
        }
      }

      void runListingSearch();
    }, 300);

    return () => {
      isCancelled = true;
      window.clearTimeout(debounceTimeoutId);
    };
  }, [listingSearchQuery, token, failWithError]);

  const activeListingRows = useMemo(() => {
    const trimmedListingSearchQuery = listingSearchQuery.trim();

    if (trimmedListingSearchQuery !== "" && searchResultRows !== null) {
      return searchResultRows;
    }

    return rows;
  }, [listingSearchQuery, searchResultRows, rows]);

  async function handleRefreshListingTable() {
    if (!token || isListingRefreshing) {
      return;
    }

    setIsListingRefreshing(true);

    try {
      const inboxListingSnapshot = await loadInboxListingSnapshot();

      if (inboxListingSnapshot !== null) {
        setRows(inboxListingSnapshot.loadedRows);
        setProjectOptions(inboxListingSnapshot.projectOptions);
      }

      const trimmedListingSearchQuery = listingSearchQuery.trim();

      if (trimmedListingSearchQuery !== "") {
        const searchResponseRows = await api.searchQuotaRequests(
          token,
          trimmedListingSearchQuery,
        );

        setSearchResultRows(searchResponseRows as QuarantineQuotaRequestRow[]);
      }
    } catch (loadError) {
      failWithError(loadError);
    } finally {
      setIsListingRefreshing(false);
    }
  }

  async function handleProjectSelectionChange(
    row: QuarantineQuotaRequestRow,
    nextSelectValue: string,
  ) {
    if (!token) {
      return;
    }

    const previousSelectValue = resolveProjectSelectValue(row);

    if (nextSelectValue === previousSelectValue) {
      return;
    }

    if (nextSelectValue === PROJECT_SELECT_NEW_PROJECT_VALUE) {
      const projectName = window.prompt("Enter a name for the new project:");

      if (projectName === null || projectName.trim() === "") {
        return;
      }

      try {
        const createdProject = await api.createProject(token, projectName.trim());
        const updatedRow = await api.patchQuotaRequest(token, row.id, {
          project_id: createdProject.id,
        });

        setProjectOptions((currentProjectOptions) => {
          const existingProject = currentProjectOptions.find(
            (projectOption) => projectOption.id === createdProject.id,
          );

          if (existingProject !== undefined) {
            return currentProjectOptions;
          }

          return [
            ...currentProjectOptions,
            {
              id: createdProject.id,
              name: createdProject.name,
              created_at_timestamp: createdProject.created_at_timestamp,
            },
          ];
        });
        applyUpdatedQuotaRequestRowToListing(updatedRow as QuarantineQuotaRequestRow);
        setRowActionErrorMessage(null);
      } catch (patchError) {
        reportRuntimeError(patchError);
        const message =
          patchError instanceof ApiError || patchError instanceof Error
            ? patchError.message
            : "unknown error";
        setRowActionErrorMessage(message);
      }

      return;
    }

    if (
      nextSelectValue === PROJECT_SELECT_UNASSIGNED_VALUE
      && row.status === WORKFLOW_STATUS_TRACKED
    ) {
      const confirmed = window.confirm(
        "Change this quota request back to quarantine and remove its project assignment?",
      );

      if (!confirmed) {
        return;
      }

      try {
        const updatedRow = await api.patchQuotaRequest(token, row.id, {
          project_id: null,
        });

        applyUpdatedQuotaRequestRowToListing(updatedRow as QuarantineQuotaRequestRow);
        setRowActionErrorMessage(null);
      } catch (patchError) {
        reportRuntimeError(patchError);
        const message =
          patchError instanceof ApiError || patchError instanceof Error
            ? patchError.message
            : "unknown error";
        setRowActionErrorMessage(message);
      }

      return;
    }

    if (nextSelectValue === PROJECT_SELECT_UNASSIGNED_VALUE) {
      return;
    }

    try {
      const updatedRow = await api.patchQuotaRequest(token, row.id, {
        project_id: nextSelectValue,
      });

      applyUpdatedQuotaRequestRowToListing(updatedRow as QuarantineQuotaRequestRow);
      setRowActionErrorMessage(null);
    } catch (patchError) {
      reportRuntimeError(patchError);
      const message =
        patchError instanceof ApiError || patchError instanceof Error
          ? patchError.message
          : "unknown error";
      setRowActionErrorMessage(message);
    }
  }

  async function handleWorkflowStatusChange(row: QuarantineQuotaRequestRow, nextStatus: string) {
    if (!token) {
      return;
    }

    if (nextStatus === row.status) {
      return;
    }

    try {
      const updatedRow = await api.patchQuotaRequest(token, row.id, {
        status: nextStatus,
      });

      applyUpdatedQuotaRequestRowToListing(updatedRow as QuarantineQuotaRequestRow);
      setRowActionErrorMessage(null);
    } catch (patchError) {
      reportRuntimeError(patchError);
      const message =
        patchError instanceof ApiError || patchError instanceof Error
          ? patchError.message
          : "unknown error";
      setRowActionErrorMessage(message);
    }
  }

  async function handlePriorityChange(row: QuarantineQuotaRequestRow, nextPriority: string) {
    if (!token) {
      return;
    }

    const currentPriority = row.priority ?? QUOTA_REQUEST_DEFAULT_PRIORITY;

    if (nextPriority === currentPriority) {
      return;
    }

    try {
      const updatedRow = await api.patchQuotaRequest(token, row.id, {
        priority: nextPriority,
      });

      applyUpdatedQuotaRequestRowToListing(updatedRow as QuarantineQuotaRequestRow);
      setRowActionErrorMessage(null);
    } catch (patchError) {
      reportRuntimeError(patchError);
      const message =
        patchError instanceof ApiError || patchError instanceof Error
          ? patchError.message
          : "unknown error";
      setRowActionErrorMessage(message);
    }
  }

  const pendingSegmentRowCount = useMemo(
    () => filterQuotaRequestRowsByInboxSegment(rows, INBOX_SEGMENT_PENDING).length,
    [rows],
  );

  const quarantineSegmentRowCount = useMemo(
    () => filterQuotaRequestRowsByInboxSegment(rows, INBOX_SEGMENT_QUARANTINE).length,
    [rows],
  );

  const segmentRows = useMemo(() => {
    const trimmedListingSearchQuery = listingSearchQuery.trim();

    if (trimmedListingSearchQuery !== "" && searchResultRows !== null) {
      return activeListingRows;
    }

    return filterQuotaRequestRowsByInboxSegment(activeListingRows, inboxSegment);
  }, [listingSearchQuery, searchResultRows, activeListingRows, inboxSegment]);

  const filteredRows = useMemo(
    () => filterQuarantineQuotaRequestRows(segmentRows, filterState, projectNameById),
    [segmentRows, filterState, projectNameById],
  );

  const sortedRows = useMemo(
    () => sortQuarantineQuotaRequestRows(filteredRows, sortState ?? undefined),
    [filteredRows, sortState],
  );

  const selectedRowCount = selectedRowIds.size;

  const allVisibleRowsSelected =
    sortedRows.length > 0 && sortedRows.every((row) => selectedRowIds.has(row.id));

  function handleToggleSelectAllVisibleRows(isSelected: boolean) {
    if (!isSelected) {
      setSelectedRowIds(new Set());

      return;
    }

    const allVisibleRowIds = new Set(sortedRows.map((row) => row.id));

    setSelectedRowIds(allVisibleRowIds);
  }

  async function handleBulkApplyAssignToProject() {
    if (!token || isBulkActionRunning || selectedRowCount === 0) {
      return;
    }

    let targetProjectId = bulkAssignProjectSelectValue;

    if (targetProjectId === BULK_ACTION_EMPTY_VALUE) {
      return;
    }

    if (targetProjectId === PROJECT_SELECT_NEW_PROJECT_VALUE) {
      const projectName = window.prompt("Enter a name for the new project:");

      if (projectName === null || projectName.trim() === "") {
        return;
      }

      try {
        setIsBulkActionRunning(true);
        const createdProject = await api.createProject(token, projectName.trim());

        targetProjectId = createdProject.id;
        setProjectOptions((currentProjectOptions) => {
          const existingProject = currentProjectOptions.find(
            (projectOption) => projectOption.id === createdProject.id,
          );

          if (existingProject !== undefined) {
            return currentProjectOptions;
          }

          return [
            ...currentProjectOptions,
            {
              id: createdProject.id,
              name: createdProject.name,
              created_at_timestamp: createdProject.created_at_timestamp,
            },
          ];
        });
        setIsBulkActionRunning(false);
      } catch (createProjectError) {
        reportRuntimeError(createProjectError);
        const message =
          createProjectError instanceof ApiError || createProjectError instanceof Error
            ? createProjectError.message
            : "unknown error";
        setRowActionErrorMessage(message);
        resetBulkSelectionAndUi();
        setIsBulkActionRunning(false);

        return;
      }
    }

    setIsBulkActionRunning(true);

    const selectedRowsInTableOrder = sortedRows.filter((row) => selectedRowIds.has(row.id));

    try {
      for (const row of selectedRowsInTableOrder) {
        const updatedRow = await api.patchQuotaRequest(token, row.id, {
          project_id: targetProjectId,
        });

        applyUpdatedQuotaRequestRowToListing(updatedRow as QuarantineQuotaRequestRow);
      }

      setRowActionErrorMessage(null);
    } catch (patchError) {
      reportRuntimeError(patchError);
      const message =
        patchError instanceof ApiError || patchError instanceof Error
          ? patchError.message
          : "unknown error";
      setRowActionErrorMessage(message);
    } finally {
      resetBulkSelectionAndUi();
      setIsBulkActionRunning(false);
    }
  }

  async function handleBulkApplySetStatus() {
    if (!token || isBulkActionRunning || selectedRowCount === 0) {
      return;
    }

    const nextStatus = bulkStatusSelectValue;

    setIsBulkActionRunning(true);

    const selectedRowsInTableOrder = sortedRows.filter((row) => selectedRowIds.has(row.id));

    try {
      for (const row of selectedRowsInTableOrder) {
        if (row.status === nextStatus) {
          continue;
        }

        const updatedRow = await api.patchQuotaRequest(token, row.id, {
          status: nextStatus,
        });

        applyUpdatedQuotaRequestRowToListing(updatedRow as QuarantineQuotaRequestRow);
      }

      setRowActionErrorMessage(null);
    } catch (patchError) {
      reportRuntimeError(patchError);
      const message =
        patchError instanceof ApiError || patchError instanceof Error
          ? patchError.message
          : "unknown error";
      setRowActionErrorMessage(message);
    } finally {
      resetBulkSelectionAndUi();
      setIsBulkActionRunning(false);
    }
  }

  async function handleBulkApplySetPriority() {
    if (!token || isBulkActionRunning || selectedRowCount === 0) {
      return;
    }

    const nextPriority = bulkPrioritySelectValue;

    setIsBulkActionRunning(true);

    const selectedRowsInTableOrder = sortedRows.filter((row) => selectedRowIds.has(row.id));

    try {
      for (const row of selectedRowsInTableOrder) {
        const currentPriority = row.priority ?? QUOTA_REQUEST_DEFAULT_PRIORITY;

        if (nextPriority === currentPriority) {
          continue;
        }

        const updatedRow = await api.patchQuotaRequest(token, row.id, {
          priority: nextPriority,
        });

        applyUpdatedQuotaRequestRowToListing(updatedRow as QuarantineQuotaRequestRow);
      }

      setRowActionErrorMessage(null);
    } catch (patchError) {
      reportRuntimeError(patchError);
      const message =
        patchError instanceof ApiError || patchError instanceof Error
          ? patchError.message
          : "unknown error";
      setRowActionErrorMessage(message);
    } finally {
      resetBulkSelectionAndUi();
      setIsBulkActionRunning(false);
    }
  }

  useEffect(() => {
    resetBulkSelectionAndUi();
  }, [inboxSegment, showClosed]);

  const isBulkActionPrimaryRowVisible = selectedRowCount > 0;

  const isBulkActionDetailRowVisible =
    selectedRowCount > 0 && bulkActionValue !== BULK_ACTION_EMPTY_VALUE;

  const sortedRowIdsKey = useMemo(
    () => sortedRows.map((row) => row.id).join("\n"),
    [sortedRows],
  );

  useEffect(() => {
    let isCancelled = false;

    async function persistInboxListing() {
      if (!token) {
        return;
      }

      if (sortedRows.length === 0) {
        setListingSortId(null);

        return;
      }

      const quotaRequestIds = sortedRows.map((row) => row.id);

      try {
        const listingBody = await api.createQuotaRequestListing(token, quotaRequestIds);

        if (!isCancelled) {
          setListingSortId(listingBody.sort_id);
        }
      } catch (listingError) {
        if (!isCancelled) {
          failWithError(listingError);
        }
      }
    }

    persistInboxListing();

    return () => {
      isCancelled = true;
    };
  }, [token, sortedRowIdsKey, sortedRows, failWithError]);

  const filterOptionsByColumn = useMemo(() => {
    const optionsByColumn: Partial<Record<QuarantineQuotaRequestFilterColumn, string[]>> = {};

    for (const column of QUARANTINE_QUOTA_REQUEST_FILTER_COLUMNS) {
      optionsByColumn[column] = collectQuarantineQuotaRequestFilterOptions(
        segmentRows,
        column,
        filterState,
        projectNameById,
        sortedProjectOptions,
      );
    }

    return optionsByColumn;
  }, [segmentRows, filterState, projectNameById, sortedProjectOptions]);

  throwIfFatalError(fatalError);

  return (
    <main style={{ fontFamily: "system-ui, sans-serif", margin: "2rem" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div>
          <h1>Quota requests</h1>
          <p>
            Quarantine rows need review before assignment; Pending lists tracked, must respond, and
            limbo requests
            (and closed when Show closed is checked).
          </p>
        </div>
        <div>
          {user ? <span style={{ marginRight: "1rem" }}>{user.email}</span> : null}
          <button type="button" onClick={logout}>Sign out</button>
        </div>
      </header>

      {isLoading ? <p>Loading…</p> : null}
      {rowActionErrorMessage !== null ? (
        <p role="alert">Error: {rowActionErrorMessage}</p>
      ) : null}

      {!isLoading && rowActionErrorMessage === null ? (
        <>
          <div
            role="tablist"
            aria-label="Inbox view"
            style={{ display: "inline-flex", marginBottom: "0.75rem" }}
          >
            <button
              type="button"
              role="tab"
              aria-label={`Pending (${pendingSegmentRowCount})`}
              aria-selected={inboxSegment === INBOX_SEGMENT_PENDING}
              style={{
                ...INBOX_SEGMENT_TAB_STYLE_BASE,
                borderTopLeftRadius: "4px",
                borderBottomLeftRadius: "4px",
                borderRight: "none",
                ...(inboxSegment === INBOX_SEGMENT_PENDING ? INBOX_SEGMENT_TAB_SELECTED_STYLE : {}),
              }}
              onClick={() => {
                setInboxSegment(INBOX_SEGMENT_PENDING);
              }}
            >
              Pending
              <span aria-hidden="true" style={INBOX_SEGMENT_TAB_BADGE_STYLE}>
                {pendingSegmentRowCount}
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-label={`Quarantine (${quarantineSegmentRowCount})`}
              aria-selected={inboxSegment === INBOX_SEGMENT_QUARANTINE}
              style={{
                ...INBOX_SEGMENT_TAB_STYLE_BASE,
                borderTopRightRadius: "4px",
                borderBottomRightRadius: "4px",
                ...(inboxSegment === INBOX_SEGMENT_QUARANTINE
                  ? INBOX_SEGMENT_TAB_SELECTED_STYLE
                  : {}),
              }}
              onClick={() => {
                setInboxSegment(INBOX_SEGMENT_QUARANTINE);
              }}
            >
              Quarantine
              <span aria-hidden="true" style={INBOX_SEGMENT_TAB_BADGE_STYLE}>
                {quarantineSegmentRowCount}
              </span>
            </button>
          </div>
          {inboxSegment === INBOX_SEGMENT_PENDING ? (
            <label style={{ display: "block", marginBottom: "0.75rem" }}>
              <input
                type="checkbox"
                checked={showClosed}
                aria-label="Show closed"
                onChange={(changeEvent) => {
                  setShowClosed(changeEvent.target.checked);
                }}
              />
              {" "}
              Show closed
            </label>
          ) : null}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 0,
              marginBottom: "0.75rem",
              minHeight: INBOX_LISTING_TOOLBAR_ROW_MIN_HEIGHT,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                minWidth: 0,
              }}
            >
              <p
                data-testid="quota-request-inbox-item-count"
                style={{ margin: 0, flexShrink: 0 }}
              >
                {sortedRows.length} item{sortedRows.length === 1 ? "" : "s"}
              </p>
              {isBulkActionPrimaryRowVisible ? (
                <span style={INBOX_TOOLBAR_FIELD_SEPARATOR_STYLE} aria-hidden="true">
                  |
                </span>
              ) : null}
              <div
                data-testid="bulk-action-bar"
                style={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  minWidth: 0,
                }}
                aria-hidden={!isBulkActionPrimaryRowVisible && !isBulkActionDetailRowVisible}
              >
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  margin: 0,
                  flexShrink: 0,
                  minWidth: BULK_ACTION_PRIMARY_SLOT_MIN_WIDTH,
                  visibility: isBulkActionPrimaryRowVisible ? "visible" : "hidden",
                }}
              >
                <span style={{ marginRight: "0.5rem" }}>Bulk action</span>
                <select
                  aria-label="Bulk action"
                  value={bulkActionValue}
                  disabled={isBulkActionRunning || !isBulkActionPrimaryRowVisible}
                  tabIndex={isBulkActionPrimaryRowVisible ? 0 : -1}
                  onChange={(changeEvent) => {
                    const nextBulkActionValue = changeEvent.target.value;

                    setBulkActionValue(nextBulkActionValue);

                    if (nextBulkActionValue === BULK_ACTION_ASSIGN_TO_PROJECT) {
                      if (sortedProjectOptions.length > 0) {
                        setBulkAssignProjectSelectValue(sortedProjectOptions[0].id);
                      } else {
                        setBulkAssignProjectSelectValue(PROJECT_SELECT_NEW_PROJECT_VALUE);
                      }
                    } else {
                      setBulkAssignProjectSelectValue(BULK_ACTION_EMPTY_VALUE);
                    }

                    if (nextBulkActionValue === BULK_ACTION_SET_STATUS) {
                      setBulkStatusSelectValue(ASSIGNED_WORKFLOW_STATUS_OPTIONS[0]);
                    }

                    if (nextBulkActionValue === BULK_ACTION_SET_PRIORITY) {
                      setBulkPrioritySelectValue(QUOTA_REQUEST_DEFAULT_PRIORITY);
                    }
                  }}
                >
                  <option value={BULK_ACTION_EMPTY_VALUE}>{BULK_ACTION_PLACEHOLDER_LABEL}</option>
                  <option value={BULK_ACTION_ASSIGN_TO_PROJECT}>
                    {BULK_ACTION_ASSIGN_TO_PROJECT_LABEL}
                  </option>
                  <option value={BULK_ACTION_SET_STATUS}>{BULK_ACTION_SET_STATUS_LABEL}</option>
                  <option value={BULK_ACTION_SET_PRIORITY}>{BULK_ACTION_SET_PRIORITY_LABEL}</option>
                </select>
              </label>
              {isBulkActionDetailRowVisible ? (
                <span style={INBOX_TOOLBAR_FIELD_SEPARATOR_STYLE} aria-hidden="true">
                  |
                </span>
              ) : null}
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  flexShrink: 0,
                  minWidth: BULK_ACTION_DETAIL_SLOT_MIN_WIDTH,
                  visibility: isBulkActionDetailRowVisible ? "visible" : "hidden",
                }}
                aria-hidden={!isBulkActionDetailRowVisible}
              >
                  {bulkActionValue === BULK_ACTION_ASSIGN_TO_PROJECT ? (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.5rem",
                        alignItems: "center",
                      }}
                    >
                      <select
                        aria-label="Bulk assign project"
                        value={bulkAssignProjectSelectValue}
                        disabled={isBulkActionRunning || !isBulkActionDetailRowVisible}
                        tabIndex={isBulkActionDetailRowVisible ? 0 : -1}
                        onChange={(changeEvent) => {
                          setBulkAssignProjectSelectValue(changeEvent.target.value);
                        }}
                        style={{ maxWidth: "14rem" }}
                      >
                        {sortedProjectOptions.map((projectOption) => (
                          <option key={projectOption.id} value={projectOption.id}>
                            {formatProjectOptionChoiceLabel(projectOption)}
                          </option>
                        ))}
                        <option value={PROJECT_SELECT_NEW_PROJECT_VALUE}>
                          {PROJECT_SELECT_NEW_PROJECT_LABEL}
                        </option>
                      </select>
                      <button
                        type="button"
                        disabled={
                          isBulkActionRunning
                          || bulkAssignProjectSelectValue === ""
                          || !isBulkActionDetailRowVisible
                        }
                        tabIndex={isBulkActionDetailRowVisible ? 0 : -1}
                        onClick={() => {
                          void handleBulkApplyAssignToProject();
                        }}
                      >
                        Apply
                      </button>
                      <button
                        type="button"
                        disabled={isBulkActionRunning || !isBulkActionDetailRowVisible}
                        tabIndex={isBulkActionDetailRowVisible ? 0 : -1}
                        onClick={() => {
                          resetBulkSelectionAndUi();
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : null}
                  {bulkActionValue === BULK_ACTION_SET_STATUS ? (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.5rem",
                        alignItems: "center",
                      }}
                    >
                      <select
                        aria-label="Bulk set status"
                        value={bulkStatusSelectValue}
                        disabled={isBulkActionRunning || !isBulkActionDetailRowVisible}
                        tabIndex={isBulkActionDetailRowVisible ? 0 : -1}
                        onChange={(changeEvent) => {
                          setBulkStatusSelectValue(changeEvent.target.value);
                        }}
                        style={{ maxWidth: "14rem" }}
                      >
                        {ASSIGNED_WORKFLOW_STATUS_OPTIONS.map((statusOption) => (
                          <option key={statusOption} value={statusOption}>
                            {formatWorkflowStatusOptionLabel(statusOption)}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        disabled={isBulkActionRunning || !isBulkActionDetailRowVisible}
                        tabIndex={isBulkActionDetailRowVisible ? 0 : -1}
                        onClick={() => {
                          void handleBulkApplySetStatus();
                        }}
                      >
                        Apply
                      </button>
                      <button
                        type="button"
                        disabled={isBulkActionRunning || !isBulkActionDetailRowVisible}
                        tabIndex={isBulkActionDetailRowVisible ? 0 : -1}
                        onClick={() => {
                          resetBulkSelectionAndUi();
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : null}
                  {bulkActionValue === BULK_ACTION_SET_PRIORITY ? (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.5rem",
                        alignItems: "center",
                      }}
                    >
                      <select
                        aria-label="Bulk set priority"
                        value={bulkPrioritySelectValue}
                        disabled={isBulkActionRunning || !isBulkActionDetailRowVisible}
                        tabIndex={isBulkActionDetailRowVisible ? 0 : -1}
                        onChange={(changeEvent) => {
                          setBulkPrioritySelectValue(changeEvent.target.value);
                        }}
                        style={{ maxWidth: "14rem" }}
                      >
                        {QUOTA_REQUEST_PRIORITY_OPTIONS.map((priorityOption) => (
                          <option key={priorityOption} value={priorityOption}>
                            {priorityOption}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        disabled={isBulkActionRunning || !isBulkActionDetailRowVisible}
                        tabIndex={isBulkActionDetailRowVisible ? 0 : -1}
                        onClick={() => {
                          void handleBulkApplySetPriority();
                        }}
                      >
                        Apply
                      </button>
                      <button
                        type="button"
                        disabled={isBulkActionRunning || !isBulkActionDetailRowVisible}
                        tabIndex={isBulkActionDetailRowVisible ? 0 : -1}
                        onClick={() => {
                          resetBulkSelectionAndUi();
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : null}
              </div>
              </div>
            </div>
            {isBulkActionPrimaryRowVisible ? (
              <span style={INBOX_TOOLBAR_FIELD_SEPARATOR_STYLE} aria-hidden="true">
                |
              </span>
            ) : null}
            <ListingTableRefreshButton
              disabled={isListingRefreshing || isSearchLoading}
              onRefresh={() => {
                void handleRefreshListingTable();
              }}
            />
            <ListingTableSearchInput
              disabled={isListingRefreshing}
              value={listingSearchQuery}
              onValueChange={setListingSearchQuery}
            />
          </div>
          <table className="data-table" style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th style={TABLE_HEADER_CELL_STYLE}>
                <input
                  type="checkbox"
                  aria-label="Select all visible quota requests"
                  checked={allVisibleRowsSelected}
                  disabled={sortedRows.length === 0}
                  onChange={(changeEvent) => {
                    handleToggleSelectAllVisibleRows(changeEvent.target.checked);
                  }}
                />
              </th>
              {QUARANTINE_TABLE_COLUMNS.flatMap((columnDefinition, columnIndex) => {
                const headerCells = [
                  <th
                    key={columnDefinition.column}
                    style={buildTableHeaderCellStyleForColumn(columnDefinition.column)}
                  >
                    <button
                      type="button"
                      style={SORTABLE_HEADER_BUTTON_STYLE}
                      aria-label={`Sort by ${columnDefinition.label}`}
                      aria-sort={
                        sortState?.column === columnDefinition.column
                          ? sortState.direction === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                      onClick={() => {
                        setSortState((currentSortState) =>
                          buildNextSortState(currentSortState, columnDefinition.column),
                        );
                      }}
                    >
                      {columnDefinition.label}
                      {formatSortIndicator(sortState, columnDefinition.column)}
                    </button>
                  </th>,
                ];

                if (columnIndex === 0) {
                  headerCells.push(
                    <th key="last_message_at" style={TABLE_HEADER_CELL_STYLE}>
                      {LAST_MESSAGE_COLUMN_LABEL}
                    </th>,
                  );
                }

                return headerCells;
              })}
            </tr>
            <tr>
              <th style={TABLE_HEADER_CELL_STYLE} />
              {QUARANTINE_TABLE_COLUMNS.flatMap((columnDefinition, columnIndex) => {
                const filterCells = [
                  <th
                    key={`filter-${columnDefinition.column}`}
                    style={buildTableHeaderCellStyleForColumn(columnDefinition.column)}
                  >
                  {isQuarantineQuotaRequestFilterColumn(columnDefinition.column) ? (
                    <select
                      aria-label={`Filter by ${QUARANTINE_FILTER_COLUMN_LABELS[columnDefinition.column]}`}
                      value={filterState[columnDefinition.column] ?? ""}
                      onChange={(changeEvent) => {
                        const nextValue = changeEvent.target.value;

                        setFilterState((currentFilterState) => {
                          const nextFilterState = { ...currentFilterState };

                          if (nextValue === "") {
                            delete nextFilterState[columnDefinition.column];
                          } else {
                            nextFilterState[columnDefinition.column] = nextValue;
                          }

                          return nextFilterState;
                        });
                      }}
                      style={buildFilterSelectStyleForColumn(columnDefinition.column)}
                    >
                      <option value="">All</option>
                      {(filterOptionsByColumn[columnDefinition.column] ?? []).map((optionValue) => (
                        <option key={optionValue} value={optionValue}>
                          {columnDefinition.column === "project"
                            ? formatProjectFilterOptionLabel(
                                optionValue,
                                sortedProjectOptions,
                                projectNameById,
                              )
                            : optionValue}
                        </option>
                      ))}
                    </select>
                  ) : null}
                </th>,
                ];

                if (columnIndex === 0) {
                  filterCells.push(
                    <th key="filter-last_message_at" style={TABLE_HEADER_CELL_STYLE} />,
                  );
                }

                return filterCells;
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              const rowBackgroundColor = getTableRowBackgroundColorForAwsStatus(row.aws_status);
              const supportCaseConsoleUrl = buildSupportCaseConsoleUrl(row);

              return (
                <tr
                  key={row.id}
                  style={
                    rowBackgroundColor !== undefined
                      ? { backgroundColor: rowBackgroundColor }
                      : undefined
                  }
                >
                  <td style={buildTableBodyCellStyle()}>
                    <input
                      type="checkbox"
                      aria-label={`Select quota request ${row.id}`}
                      checked={selectedRowIds.has(row.id)}
                      onChange={(changeEvent) => {
                        handleRowSelectionToggle(row.id, changeEvent.target.checked);
                      }}
                    />
                  </td>
                  <td style={buildTableBodyCellStyle()}>
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      <Link
                        to={
                          listingSortId === null
                            ? `/quota_request/${row.id}/message/list`
                            : `/quota_request/${row.id}/message/list?sort_id=${encodeURIComponent(listingSortId)}`
                        }
                        style={{ textDecoration: "underline", color: "inherit" }}
                      >
                        {formatRequestedAtDateOnly(resolveRequestedAtEpochSeconds(row))}
                      </Link>
                      {supportCaseConsoleUrl !== null ? (
                        <SupportCaseConsoleLinkIcon supportCaseConsoleUrl={supportCaseConsoleUrl} />
                      ) : null}
                    </span>
                  </td>
                  <td style={buildTableBodyCellStyle()}>
                    {formatLastMessageAtRelativePhrase(row.last_message_at_timestamp)}
                  </td>
                  <td style={buildTableBodyCellStyle(ENVIRONMENT_COLUMN_CELL_STYLE)}>
                    {row.deployment_environment_name}
                  </td>
                  <td style={buildTableBodyCellStyle(REGION_COLUMN_CELL_STYLE)}>
                    {row.aws_region}
                  </td>
                  <td style={buildTableBodyCellStyle(QUOTA_NAME_COLUMN_CELL_STYLE)}>
                    {resolveQuotaNameDisplayForRow(row)}
                  </td>
                  <td style={buildTableBodyCellStyle()}>
                    {row.aws_status}
                    {isSupportCaseMissingForRow(row) ? (
                      <div style={{ color: "#b00020", fontSize: "0.85rem", marginTop: "0.25rem" }}>
                        {SUPPORT_CASE_MISSING_LABEL}
                      </div>
                    ) : null}
                  </td>
                  <td style={buildTableBodyCellStyle(REQUESTED_VALUE_COLUMN_CELL_STYLE)}>
                    {row.requested_value ?? "—"}
                  </td>
                  <td style={buildTableBodyCellStyle()}>
                    <select
                      aria-label={`Project for quota request ${row.id}`}
                      value={resolveProjectSelectValue(row)}
                      onChange={(changeEvent) => {
                        void handleProjectSelectionChange(row, changeEvent.target.value);
                      }}
                      style={{ width: "100%", maxWidth: "14rem" }}
                    >
                      {shouldShowUnassignedProjectOption(row.status) ? (
                        <option value={PROJECT_SELECT_UNASSIGNED_VALUE}>
                          {PROJECT_DISPLAY_UNASSIGNED_LABEL}
                        </option>
                      ) : null}
                      {sortedProjectOptions.map((projectOption) => (
                        <option key={projectOption.id} value={projectOption.id}>
                          {formatProjectOptionChoiceLabel(projectOption)}
                        </option>
                      ))}
                      {shouldShowNewProjectOption(row.status) ? (
                        <option value={PROJECT_SELECT_NEW_PROJECT_VALUE}>
                          {PROJECT_SELECT_NEW_PROJECT_LABEL}
                        </option>
                      ) : null}
                    </select>
                  </td>
                  <td style={buildTableBodyCellStyle()}>
                    {row.status === WORKFLOW_STATUS_QUARANTINE
                      || resolveProjectSelectValue(row) === PROJECT_SELECT_UNASSIGNED_VALUE ? (
                        <span>{WORKFLOW_STATUS_QUARANTINE}</span>
                      ) : (
                        <select
                          aria-label={`Status for quota request ${row.id}`}
                          value={row.status}
                          onChange={(changeEvent) => {
                            void handleWorkflowStatusChange(row, changeEvent.target.value);
                          }}
                        >
                          {ASSIGNED_WORKFLOW_STATUS_OPTIONS.map((statusOption) => (
                            <option key={statusOption} value={statusOption}>
                              {formatWorkflowStatusOptionLabel(statusOption)}
                            </option>
                          ))}
                        </select>
                      )}
                  </td>
                  <td style={buildTableBodyCellStyle()}>
                    <select
                      aria-label={`Priority for quota request ${row.id}`}
                      value={row.priority ?? QUOTA_REQUEST_DEFAULT_PRIORITY}
                      onChange={(changeEvent) => {
                        void handlePriorityChange(row, changeEvent.target.value);
                      }}
                    >
                      {QUOTA_REQUEST_PRIORITY_OPTIONS.map((priorityOption) => (
                        <option key={priorityOption} value={priorityOption}>
                          {priorityOption}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </>
      ) : null}

      {!isLoading && rowActionErrorMessage === null && segmentRows.length === 0 ? (
        <p>
          {inboxSegment === INBOX_SEGMENT_QUARANTINE
            ? "No quarantined quota requests."
            : "No pending quota requests."}
        </p>
      ) : null}

      {!isLoading
        && rowActionErrorMessage === null
        && segmentRows.length > 0
        && sortedRows.length === 0 ? (
        <p>No rows match the current filters.</p>
      ) : null}
    </main>
  );
}
